import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  LayoutDashboard, 
  Heart,
  TrendingUp,
  Coins,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

// Dashboard Layout Component
function DashboardLayout({ children, auth }) {
  const navigate = useNavigate();
  const { user, logout } = auth;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/association', icon: LayoutDashboard, label: 'Vue d\'ensemble', end: true },
    { to: '/association/campaigns', icon: Heart, label: 'Campagnes' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col fixed h-full">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
              <p className="text-xs text-orange-600 font-medium">Association</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-700 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.company_name || user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
}

// Overview Page
function OverviewPage({ auth }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, user } = auth;

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${API}/campaigns`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          setCampaigns(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [token]);

  const totalCollected = campaigns.reduce((sum, c) => sum + c.current_amount, 0);
  const totalGoal = campaigns.reduce((sum, c) => sum + c.goal_amount, 0);
  const activeCampaigns = campaigns.filter(c => c.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="association-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Bienvenue, {user?.company_name || user?.name}
        </h1>
        <p className="text-slate-600">Tableau de bord de votre association</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Campagnes actives</p>
                <p className="text-3xl font-bold text-slate-800 counter">{activeCampaigns}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total collecté</p>
                <p className="text-3xl font-bold text-emerald-700 counter">{totalCollected.toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Objectif total</p>
                <p className="text-3xl font-bold text-orange-600 counter">{totalGoal.toFixed(2)}€</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Campaigns */}
      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vos campagnes
          </CardTitle>
          <NavLink to="/association/campaigns">
            <Button variant="outline" size="sm" className="rounded-full">
              Voir tout
            </Button>
          </NavLink>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune campagne créée</p>
              <NavLink to="/association/campaigns">
                <Button className="mt-4 bg-orange-600 hover:bg-orange-700 rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une campagne
                </Button>
              </NavLink>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.slice(0, 3).map((campaign) => {
                const progress = (campaign.current_amount / campaign.goal_amount) * 100;
                return (
                  <div key={campaign.campaign_id} className="p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-800">{campaign.title}</h4>
                        <p className="text-sm text-slate-500">{campaign.description.substring(0, 60)}...</p>
                      </div>
                      <span className={`badge ${campaign.is_active ? 'badge-success' : 'badge-warning'}`}>
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Progression</span>
                      <span className="font-semibold text-emerald-700">
                        {campaign.current_amount.toFixed(2)}€ / {campaign.goal_amount.toFixed(2)}€
                      </span>
                    </div>
                    <Progress value={Math.min(progress, 100)} className="h-2" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Impact Image */}
      <div className="mt-8 rounded-2xl overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1708593343700-a101f8fe4d11?w=1200&h=400&fit=crop" 
          alt="Impact communautaire"
          className="w-full h-64 object-cover"
        />
      </div>
    </div>
  );
}

// Campaigns Page
function CampaignsPage({ auth }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goal_amount: '',
    image_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchCampaigns = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/campaigns`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        setCampaigns(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/campaigns`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          goal_amount: parseFloat(formData.goal_amount)
        }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la création');

      toast.success('Campagne créée avec succès');
      setIsOpen(false);
      setFormData({ title: '', description: '', goal_amount: '', image_url: '' });
      fetchCampaigns();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!confirm('Désactiver cette campagne ?')) return;

    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      toast.success('Campagne désactivée');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div data-testid="campaigns-manage-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestion des campagnes
          </h1>
          <p className="text-slate-600">Créez et gérez vos campagnes de collecte</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-600 hover:bg-orange-700 rounded-full" data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle campagne
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle campagne</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre de la campagne</Label>
                <Input
                  placeholder="Aide aux enfants défavorisés"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="campaign-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Décrivez votre campagne..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="campaign-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Objectif (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="5000.00"
                  value={formData.goal_amount}
                  onChange={(e) => setFormData({ ...formData, goal_amount: e.target.value })}
                  required
                  data-testid="campaign-goal-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL de l'image (optionnel)</Label>
                <Input
                  placeholder="https://..."
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={submitting}
                  data-testid="submit-campaign-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardContent className="text-center py-12">
            <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune campagne créée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {campaigns.map((campaign) => {
            const progress = (campaign.current_amount / campaign.goal_amount) * 100;
            return (
              <Card key={campaign.campaign_id} className="bg-white border-stone-200 rounded-2xl overflow-hidden">
                {campaign.image_url && (
                  <img 
                    src={campaign.image_url} 
                    alt={campaign.title}
                    className="w-full h-40 object-cover"
                  />
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {campaign.title}
                      </h3>
                      <span className={`badge ${campaign.is_active ? 'badge-success' : 'badge-warning'}`}>
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(campaign.campaign_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <p className="text-slate-600 text-sm mb-4">{campaign.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Collecté</span>
                      <span className="font-semibold text-emerald-700">
                        {campaign.current_amount.toFixed(2)}€ / {campaign.goal_amount.toFixed(2)}€
                      </span>
                    </div>
                    <Progress value={Math.min(progress, 100)} className="h-2" />
                    <p className="text-xs text-slate-400 mt-1">{progress.toFixed(1)}% de l'objectif</p>
                  </div>

                  <p className="text-xs text-slate-400">
                    Créée le {new Date(campaign.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Main Association Dashboard Component
export default function AssociationDashboard({ auth }) {
  return (
    <DashboardLayout auth={auth}>
      <Routes>
        <Route index element={<OverviewPage auth={auth} />} />
        <Route path="campaigns" element={<CampaignsPage auth={auth} />} />
      </Routes>
    </DashboardLayout>
  );
}
